import { Button } from '@/app/common/components/button/Button'
import { Input } from '@/app/common/components/input/Input'
import { SubmitButton } from '@/common/components/SubmitButton.tsx'
import { PlusIcon } from '@/common/icons/general.tsx'
import {
  HelpLinkFormSchema,
  HelpLinkFormValues,
} from '@/pages/setup/link_handlers/help_links/help_links.schema.ts'
import { useCreateHelpLink } from '@/queries/help_links/hooks.ts'
import { Dialog, DialogContent, DialogTrigger } from '@/shad/ui/dialog.tsx'
import { useToast } from '@/shad/ui/toast/use-toast.ts'
import { RootState } from '@/storage/store.ts'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'

interface Props {
  onAddHelpLinkSuccess: () => void
}

const AddHelpLinkPopup = (props: Props) => {
  const { onAddHelpLinkSuccess } = props
  const [open, setOpen] = useState(false)

  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)

  const { toast } = useToast()

  const createHelpLink = useCreateHelpLink({
    onSuccess: () => {
      toast({
        title: 'Help Link Created',
        description: 'The help link has been created successfully.',
        variant: 'default',
      })
      onAddHelpLinkSuccess()
      setOpen(false)
      reset()
    },
    onError: () => {
      toast({
        title: 'Error',
        description: `A handbook link for this provider has already been created.`,
        variant: 'destructive',
      })
    },
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<HelpLinkFormValues>({
    resolver: zodResolver(HelpLinkFormSchema),
    defaultValues: {
      link: '',
    },
  })

  const onSubmit = (data: HelpLinkFormValues) => {
    createHelpLink.mutate({
      program_id: selectedProgramYear?.id ?? 0,
      link: data.link,
    })
  }

  const handleClose = () => {
    setOpen(false)
    reset()
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button className="h-10 w-10 rounded-full">
          <PlusIcon />
        </Button>
      </DialogTrigger>

      <DialogContent>
        <h2 className="font-medium">Add New Help Link</h2>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label>Help Link</label>
            <Input
              type="text"
              {...register('link')}
              aria-invalid={errors.link ? 'true' : 'false'}
            />
            {errors.link && (
              <p className="text-sm text-red-500 mt-1">{errors.link.message}</p>
            )}
          </div>
          <div className="flex space-x-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <div>
              <SubmitButton
                buttonTxt="Add Help Link"
                status={createHelpLink.status}
              />
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
export default AddHelpLinkPopup
